import * as React from 'react';
import { useEffect, useMemo } from 'react';
import ButtonBase from '../../BaseComponents/Button';
import cn from 'classnames';

import { Money } from '../../Money';
import { getMinPrices } from '../../Results/utils';
import { useTheme } from '../../theme';
import { MinPrice, OnFareSelect } from '../../Results/types';
import { SelectedFare } from '../../DesktopFlight/types';
import { MobileSegmentFlat } from '../../MobileSegmentInfo';
import { useTranslation } from 'react-i18next';
import {
	AviaSearchParameters,
	FareFamilyFragment,
	ResultsLegFragment,
	SegmentWithTransferFragment,
	FlightClass,
	InfoMessagePositionsEnum,
	AviaPassengerFareFragment
} from '@websky/graphql';
import DesktopFlightModalContent from '../../DesktopFlight/components/DesktopFlightModalContent/DesktopFlightModalContent';
import { useDesktopFlightWithPopup } from '../../DesktopFlight/components/DesktopFlightPopup/hooks';
import PriceInMiles from '../../PriceInMiles/PriceInMiles';
import { Collapse } from '@mui/material';
import { getTotalDurationLabel } from '../../DesktopFlightRoute/utils';
import Tooltip from '../../BaseComponents/Tooltip';
import { useConfig } from '../../context';
import { MobileSegmentRenderers } from '../../MobileSegmentInfo/components/MobileSegmentFlat/MobileSegmentFlat';
import TransitTooltip from '../../TransitTooltip';
import { TransitTooltipContext } from '../../TransitTooltip/utils';
import InfoMessages from '../../InfoMessages';
import { CdnIcon } from '@shared/ui';
import { TaxesTooltip } from '@entities/fare/ui';
import { buildFareBreakdown } from '@entities/fare';
import { DesktopFlightFullscreen } from '../../DesktopFlight';
import { useRenderers } from '../../renderProps';
import { CheckinCard, CheckinCardItem } from '@shared/CheckinCard';
import { flightCardDataAttributes, hasCanceledSegment } from '@shared/lib/flight';
import { useIsTablet } from '../../utils';
import StopsInfo from '../../DesktopFlightRoute/components/Segment/StopsInfo/StopsInfo';
import DesktopFlightModalContentSelected from '../../DesktopFlight/components/DesktopFlightModalContentSelected/DesktopFlightModalContentSelected';

export interface FlightProps extends MobileSegmentRenderers {
	legId?: number;
	flightIndex?: number;
	flight: ResultsLegFragment;
	flightIcon?: React.ReactNode;
	showAirplane?: boolean;
	onSelect?: OnFareSelect;
	selectedFares?: SelectedFare[];
	passengerFares?: AviaPassengerFareFragment[];
	preselectedFlightClass?: FlightClass;
	isRecommended?: boolean;
	isPreOpen?: boolean;
	singleFlight?: boolean;
	hidePrice?: boolean;
	readonly?: boolean;
	searchParameters?: AviaSearchParameters;
	isSelected?: boolean;
	onClear?: () => void;
	hideOnlyPrice?: boolean;
	isReturnFlight?: boolean;
	canceled?: boolean;
	orderId?: string;
	isOrderLoading?: boolean;
	isFaresMilesMustBeRequested?: boolean;
	onSubsidySuggestPopupOpen?: (next: () => void) => void;
	onSetViewFlight?: React.Dispatch<ResultsLegFragment>;
	isSelectorInModal?: boolean;
	isFareRulesDisabled?: boolean;
	renderSelectButton?: (props: {
		readonly?: boolean;
		isSelected?: boolean;
		hidePrice?: boolean;
		prices: MinPrice[];
	}) => React.ReactNode;
}

const Flight: React.FunctionComponent<FlightProps> = props => {
	const { t } = useTranslation('FlightPrice');
	const { t: tDesktopFlightInfo } = useTranslation('DesktopFlightInfo');
	const {
		DesktopFlightRouteMobileModal: DesktopFlightRouteMobileModalOverride = DesktopFlightModalContent
	} = useRenderers();
	const {
		legId,
		showAirplane,
		flight,
		selectedFares = [],
		passengerFares = [],
		isPreOpen,
		searchParameters,
		isFaresMilesMustBeRequested,
		isSelected,
		isSelectorInModal,
		isFareRulesDisabled,
		onSubsidySuggestPopupOpen,
		renderSelectButton
	} = props;

	const {
		subStatuses,
		upgradeOptions,
		economyFareGroups,
		businessFareGroups,
		milesFareGroups,
		flightSegments,
		lastSelectedFare: lastSelectedFareId,
		isModalOpen,
		openModal,
		closeModal,
		getOnFareSelect
	} = useDesktopFlightWithPopup(props);

	useEffect(() => {
		if (isModalOpen && props.onSetViewFlight) {
			props.onSetViewFlight(flight);
		}
	}, [isModalOpen]);

	const { FlightStyles: theme } = useTheme('MobileFlight');

	const [open, setOpen] = React.useState(props.readonly);

	const { isTransitTariff } = React.useContext(TransitTooltipContext);

	const {
		global: {
			companyInfo: { subsidyFaresConditionURL }
		}
	} = useConfig();

	const flightId = flight.pricesForFareGroups?.[0]?.prices?.[0]?.flight?.id;

	const hasCanceledFlightSegment = hasCanceledSegment(flight.segments);

	const [fareFamily, selectedFare] = useMemo<[FareFamilyFragment, SelectedFare]>(() => {
		if (!props.isSelected) {
			return [null, null];
		}

		const fareFamily = flight.pricesForFareGroups[0]?.fareFamily;

		return [fareFamily, selectedFares[0]];
	}, [flight, selectedFares, props.isSelected]);

	const breakdown = useMemo(
		() =>
			buildFareBreakdown({
				passengerFares: passengerFares ?? [],
				total: selectedFare?.prices?.[0]?.price
			}),
		[passengerFares, selectedFare]
	);

	const [price, originalPrice, priceInMiles, prices] = useMemo(() => {
		const prices = getMinPrices(
			flight.pricesForFareGroups,
			true,
			selectedFares,
			false,
			props.preselectedFlightClass
		);

		const minOriginalPrice = getMinPrices(
			flight.pricesForFareGroups,
			true,
			selectedFares,
			true,
			props.preselectedFlightClass
		);

		const minPrice = prices[0] || prices[1];

		return [
			minPrice,
			minOriginalPrice[0]?.price?.amount > minPrice?.price?.amount && minOriginalPrice[0]?.price,
			prices[0]?.priceInMiles,
			prices
		];
	}, [flight, selectedFares, props.preselectedFlightClass]);

	const renderStopsInfo = (segments: SegmentWithTransferFragment[]) =>
		segments.map((segmentWithTransfer, index) => {
			const stops = segmentWithTransfer.segment.stops ?? [];
			const transferDuration = segmentWithTransfer.transferDuration;

			if (!stops.length && !transferDuration) {
				return null;
			}

			return (
				<StopsInfo
					key={`${segmentWithTransfer.segment.flightNumber}_${index}`}
					stops={stops}
					stopCityName={segmentWithTransfer.segment.arrival.airport.city.name}
					transferDuration={transferDuration}
					className={theme.stopsInfo}
				/>
			);
		});

	useEffect(() => {
		if (isPreOpen) {
			closeModal();
		}
	}, [isPreOpen]);

	const onExpandHandler = () => setOpen(prev => !prev);

	const renderSegment = (
		segments: SegmentWithTransferFragment[],
		segmentId: string,
		last?: boolean,
		first?: boolean,
		multipleSegment?: boolean
	) => {
		/**
		 * WEBSKY-6907: 'flightSearch - отображаем аналогично flight`,
		 * но только на страницах результатов поиска и выбранных рейсах
		 * не отображаем на странице управления заказом
		 */
		const infoMessagesPositions =
			window !== undefined && window.location.href.includes('/results')
				? [InfoMessagePositionsEnum.flightSearch, InfoMessagePositionsEnum.flight]
				: [InfoMessagePositionsEnum.flight];

		const isCanceled = hasCanceledSegment(segments);

		return (
			<CheckinCardItem
				key={segmentId}
				className={theme.segment__container}
				classes={{ content: theme.checkinCardItem__content }}
				delimiter={!last ? 'dashed' : undefined}
			>
				{first && (
					<InfoMessages
						position={infoMessagesPositions}
						className={theme.flightInfoMessage}
						itemHtmlClassName={theme.flightInfoMessage__htmlMessage}
						messages={flight.segments[0].segment.messages}
					/>
				)}

				<MobileSegmentFlat
					segments={segments}
					showAirplaneInfo={(flightSegments.length === 1 && !selectedFare) || open}
					className={theme.segment}
					first={first}
					last={last}
					multipleSegments={multipleSegment}
					totalDuration={
						flightSegments.length > 1 && last && open ? (
							<span>{getTotalDurationLabel(tDesktopFlightInfo, flight)}</span>
						) : null
					}
					renderCompanyName={props.renderCompanyName}
					isReturnFlight={props.isReturnFlight}
					canceled={isCanceled}
					onClickSegment={openModal}
				/>

				{selectedFare && !open && !isCanceled && !isTablet && renderStopsInfo(segments)}
			</CheckinCardItem>
		);
	};

	const renderSubsidyInfo = () => {
		return (
			<Tooltip title={t('A subsidized fare requires all passengers on the booking to have benefits.')}>
				<a
					href={subsidyFaresConditionURL}
					onClick={e => e.stopPropagation()}
					target="_blank"
					className={theme.subsidies}
				>
					{t('Subsidized flight')} <CdnIcon icon="Info/Details" />
				</a>
			</Tooltip>
		);
	};

	const isExpandable = flightSegments.length > 1 && !props.readonly && !props.canceled;

	const isTablet = useIsTablet();

	const lastSelectedFare = useMemo(() => {
		return flight.pricesForFareGroups.find(fareGroup => fareGroup?.fareFamily?.id === lastSelectedFareId)
			?.fareFamily;
	}, [flight.pricesForFareGroups, lastSelectedFareId]);

	return (
		<CheckinCard
			className={cn({
				[theme.container]: isTablet,
				[theme.flight_withAircraft]: showAirplane,
				[theme.expanded]: isExpandable,
				[theme.root]: isTablet,
				[theme.selected]: props.isSelected
			})}
			{...flightCardDataAttributes({
				testId: 'mobile-flight',
				segments: flight.segments,
				flightIndex: props.flightIndex,
				selected: props.isSelected,
				recommended: props.isRecommended
			})}
		>
			<>
				{props.flightIcon}
				<CheckinCardItem
					delimiter="dashed"
					delimiterGap
					className={cn(theme.segments, {
						[theme.segments_expanded]: open,
						[theme.checkinCardItem__container]: open
					})}
					classes={{ content: theme.checkinCardItem__content }}
				>
					{flightSegments.length > 1 && (
						<>
							<Collapse in={open}>
								<div className={theme.segments__expanded}>
									{flightSegments.map((segment, index) => {
										return renderSegment(
											[segment],
											`c_${index}`,
											index === flightSegments.length - 1,
											index === 0,
											false
										);
									})}
								</div>
							</Collapse>

							{!open && renderSegment(flightSegments, '0', true, false, true)}

							{isExpandable && (
								<div
									className={cn(theme.expand, {
										[theme.expand_open]: open
									})}
									onClick={onExpandHandler}
								>
									<CdnIcon icon="Arrow/Expand_more" />
								</div>
							)}
						</>
					)}

					{flightSegments.length === 1 && renderSegment(flightSegments, '0', true, true)}
				</CheckinCardItem>

				{renderSelectButton?.({
					readonly: props.readonly,
					isSelected: props.isSelected,
					hidePrice: props.hidePrice,
					prices
				}) ?? (
					<>
						{!props.readonly && !props.isSelected && (
							<CheckinCardItem className={theme.price__wrapper}>
								{isTransitTariff && (
									<div className={theme.transitTariff}>
										<TransitTooltip />
									</div>
								)}

								{originalPrice && (
									<div className={theme.price_original}>
										<Money money={originalPrice} moneyClassName={theme.price_originalMoney} />
									</div>
								)}

								<div>
									<ButtonBase
										variant="optionAction"
										className={theme.price}
										data-test-id="mobile-flight-select"
										onClick={
											onSubsidySuggestPopupOpen
												? () => onSubsidySuggestPopupOpen(openModal)
												: openModal
										}
									>
										{!props.hidePrice && !props.hideOnlyPrice ? (
											<div className={theme.price__text}>
												<span>{t('Select from')}</span>
												{priceInMiles ? (
													<PriceInMiles
														amount={priceInMiles}
														moneyClassName={cn(theme.price__money, theme.price__miles)}
														currencyClassName={theme.currency}
													/>
												) : (
													<Money moneyClassName={theme.price__money} money={price?.price} />
												)}
											</div>
										) : (
											<div className={theme.price__text}>
												<span>{t('Select')}</span>
											</div>
										)}
									</ButtonBase>

									<InfoMessages
										position={InfoMessagePositionsEnum.flightPrice}
										className={theme.infoMessage}
										itemHtmlClassName={theme.infoMessage__htmlMessage}
										messages={flight.segments[0].segment.messages}
									/>
								</div>
							</CheckinCardItem>
						)}
					</>
				)}

				{selectedFare && !subStatuses.length && (
					<CheckinCardItem className={theme.selectedFare}>
						<div>
							{prices[0]?.hasSubsidyFare && renderSubsidyInfo()}
							<div>{fareFamily?.title}</div>
							{hasCanceledFlightSegment && (
								<div className={theme.canceled}>{tDesktopFlightInfo('Canceled')}</div>
							)}
						</div>
						<div className={theme.selectedFare__priceWrapper}>
							{!props.hidePrice && !props.hideOnlyPrice && (
								<>
									<Money
										moneyClassName={theme.selectedFare__price}
										money={selectedFare.prices[0].price}
										isLoading={props.isOrderLoading}
									/>
									{breakdown.groups.length > 0 && <TaxesTooltip breakdown={breakdown} />}
								</>
							)}

							{props.onClear && (
								<div onClick={props.onClear} className={theme.price__close}>
									<CdnIcon icon="Action/Close-Clear" />
								</div>
							)}
						</div>
					</CheckinCardItem>
				)}

				{!!subStatuses.length && (
					<div className={theme.subStatus}>
						<span>{t(`DesktopFlightInfo:SubStatus_${subStatuses[0]}`)}</span>
					</div>
				)}

				{priceInMiles && (
					<div className={theme.miles__taxes}>
						{t('taxes and fees')}:<Money money={price.price} />
					</div>
				)}

				<DesktopFlightFullscreen isOpen={isModalOpen} onClose={closeModal} isSelected={isSelected}>
					{isSelected ? (
						<DesktopFlightModalContentSelected
							legId={legId}
							flightId={flightId}
							lastSelectedFare={lastSelectedFare}
							passengerFares={passengerFares}
							flight={flight}
							orderId={props.orderId}
							onClose={closeModal}
							isReturnFlight={props.isReturnFlight}
							isSelectorInModal={isSelectorInModal}
							hidePrice={props.hidePrice}
							isFareRulesDisabled={isFareRulesDisabled}
							isFaresMilesMustBeRequested={isFaresMilesMustBeRequested}
						/>
					) : (
						<DesktopFlightRouteMobileModalOverride
							legId={legId}
							lastSelectedFare={lastSelectedFare}
							selectedFares={selectedFares}
							upgradeOptions={upgradeOptions}
							flight={flight}
							flightId={flightId}
							orderId={props.orderId}
							economyFares={economyFareGroups}
							businessFares={businessFareGroups}
							milesFares={milesFareGroups}
							getOnFareSelect={getOnFareSelect}
							hidePrice={props.hidePrice}
							searchParameters={searchParameters}
							preselectedFlightClass={props.preselectedFlightClass}
							onClose={closeModal}
							isFaresMilesMustBeRequested={isFaresMilesMustBeRequested}
						/>
					)}
				</DesktopFlightFullscreen>
			</>
		</CheckinCard>
	);
};

export default Flight;
